import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Link, Grid, IconButton, useTheme } from '@mui/material';
import { Facebook, Twitter, Email, Phone } from '@mui/icons-material';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Create a custom axios instance that suppresses error logging
const silentAxios = axios.create();
silentAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Suppress error logging by returning a rejected promise without logging
    return Promise.reject(error);
  }
);

const Footer = () => {
  const theme = useTheme();
  const year = new Date().getFullYear();
  
  // State for dynamic menu data
  const [menuData, setMenuData] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState(null);

  // Fetch hierarchical menu data
  useEffect(() => {
    fetchMenuData();
  }, []);

  const fetchMenuData = async () => {
    try {
      setMenuLoading(true);
      setMenuError(null);
      // Use silentAxios to prevent error logging
      const response = await silentAxios.get(`${apiUrl}/menus`, {
        validateStatus: function (status) {
          // Don't throw error for any status code
          return true;
        }
      });
      
      if (response.status === 200 && response.data) {
        setMenuData(response.data);
      } else {
        // Use fallback menu if request fails
        setMenuData([
          { id: 1, name: "Home", url: "/", children: [] },
          { id: 2, name: "Articles", url: "/articles", children: [] },
          { id: 3, name: "About", url: "/about", children: [] },
          { id: 4, name: "Contact", url: "/contact", children: [] },
          { id: 5, name: "Privacy Policy", url: "/privacy", children: [] },
          { id: 6, name: "Terms of Service", url: "/terms", children: [] },
        ]);
      }
    } catch (err) {
      // Silently handle error - use fallback menu
      setMenuData([
        { id: 1, name: "Home", url: "/", children: [] },
        { id: 2, name: "Articles", url: "/articles", children: [] },
        { id: 3, name: "About", url: "/about", children: [] },
        { id: 4, name: "Contact", url: "/contact", children: [] },
        { id: 5, name: "Privacy Policy", url: "/privacy", children: [] },
        { id: 6, name: "Terms of Service", url: "/terms", children: [] },
      ]);
    } finally {
      setMenuLoading(false);
    }
  };

  // Apply menu item styling from database
  const getMenuItemStyle = (menuItem) => ({
    fontFamily: menuItem.font_family || "inherit",
    fontSize: menuItem.font_size || "inherit",
    fontWeight: menuItem.font_weight || "inherit",
    color: menuItem.color || "text.secondary",
    fontStyle: menuItem.style || "inherit",
    textDecoration: menuItem.style === "underline" ? "underline" : "none",
  });

  // Filter menu items for footer - only show important/main items
  const getFilteredMenuItems = () => {
    if (!menuData || menuData.length === 0) return { secondary: [] };

    const legalMenuNames = [
      'privacy', 'policy'
    ];

    const secondaryItems = menuData.filter(item => {
      const itemName = item.name.toLowerCase();
      return legalMenuNames.some(legal => itemName.includes(legal));
    }).slice(0, 2);

    return { secondary: secondaryItems };
  };

  // Render clean menu item (no children, just main links)
  const renderCleanMenuItem = (menuItem) => {
    const menuStyle = getMenuItemStyle(menuItem);
    const isInternal = menuItem.url && menuItem.url.startsWith('/');

    return (
      <Link 
        key={menuItem.id}
        component={isInternal ? RouterLink : 'a'}
        to={isInternal ? menuItem.url : undefined}
        href={!isInternal ? (menuItem.url || "#") : undefined} 
        color="text.secondary" 
        sx={{ 
          ...menuStyle,
          display: 'block',
          mb: 1,
          '&:hover': {
            color: 'primary.main',
          }
        }}
      >
        {menuItem.name}
      </Link>
    );
  };

  const { secondary: secondaryMenuItems } = getFilteredMenuItems();

  // Static fallback menu items if no suitable dynamic items found
  const staticMainItems = [];

  const staticSecondaryItems = [
    { id: 'static-privacy', name: 'Privacy Policy', url: '/privacy-policy' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        px: 2,
        mt: 'auto',
        backgroundColor: theme.palette.mode === 'light' 
          ? 'rgba(232, 236, 241, 0.8)' 
          : 'rgba(0, 0, 0, 0.1)',
        borderTop: `1px solid ${theme.palette.divider}`
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info and Social Media */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Zaror Sports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your premier destination for custom sports kit design and personalized team apparel.
            </Typography>
            
            {/* Contact Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                For any inquiries please email or call us:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Email sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                <Link 
                  href="mailto:info@zaror.co.uk" 
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                >
                  info@zaror.co.uk
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Phone sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                <Link 
                  href="tel:08006351020" 
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                >
                  0800 635 1020
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Phone sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                <Link 
                  href="tel:01216901020" 
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                >
                  0121 690 1020
                </Link>
              </Box>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <IconButton 
                color="primary" 
                aria-label="facebook"
                component="a"
                href="https://www.facebook.com/zarorsports"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook />
              </IconButton>
              <IconButton 
                color="primary" 
                aria-label="twitter"
                component="a"
                href="https://x.com/zarorsports"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Twitter />
              </IconButton>
            </Box>
          </Grid>
          
          {/* Policies */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Policies
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {(menuLoading ? staticSecondaryItems : (secondaryMenuItems.length > 0 ? secondaryMenuItems : staticSecondaryItems))
                .map((menuItem) => renderCleanMenuItem(menuItem))}
            </Box>
          </Grid>
        </Grid>
        
        {/* Clean Copyright Section */}
        <Box sx={{ mt: 5, pt: 3, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {year} Zaror Sports. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;

